import { Injectable } from '@angular/core';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { QuizModalComponent } from '@shared/modals/quiz-modal/quiz-modal.component';
import { ModalService } from '@core/services/modal/modal.service';
import { Overlay } from '@angular/cdk/overlay';
import { SessionStorageService } from '@core/services/session-storage/session-storage.service';
import { STORAGE_NAMES, VIEW_TYPES } from '@root/app.config';
import { ShareService } from '@core/services/share-data/share-data.service';
import { filter, skip, take } from 'rxjs';

@Injectable({
	providedIn: 'root',
})
export class QuizService {
	quizStatuses = this.sessionStorageService.getSession(STORAGE_NAMES.zip_quiz_status) || {
		dyb: false,
		iv: false,
		detection: false,
	};

	constructor(
		private matDialog: MatDialog,
		private modalService: ModalService,
		private overlay: Overlay,
		private sessionStorageService: SessionStorageService,
		private shareService: ShareService,
	) {}

	showQuizIfNeeded(context: string, toSelectOpeningScreen: boolean = null): void {
		if (context) {
			const quizModal = this.matDialog.getDialogById(QuizModalComponent.ID);

			let closeMethod = '';
			if (context === 'restart_detection') {
				closeMethod = context;
			} else if (context === 'add_detection' || toSelectOpeningScreen) {
				closeMethod = 'to_sos';
			} else if (context === 'project_sample' && toSelectOpeningScreen === false) {
				closeMethod = 'new_background';
			} else {
				closeMethod = 'view_change';
			}
			quizModal?.close({ closeMethod });
		}

		if (context === 'upload') {
			if (!this.quizStatuses.detection) {
				let detectionError = false;
				this.shareService.getDetectionError.pipe(take(1)).subscribe(() => (detectionError = true));
				this.shareService.getDetectionDone
					.pipe(
						skip(1),
						filter((detectionDone) => detectionDone),
						take(1),
						filter(() => !detectionError),
					)
					.subscribe(() => {
						this.quizStatuses.detection = true;
						this.refreshQuiz();
					});
			}

			if (!this.quizStatuses.iv) {
				this.shareService.getSelectOpeningMode
					.pipe(
						skip(1),
						filter((selectOpeningMode) => !selectOpeningMode),
						take(1),
					)
					.subscribe(() => {
						this.quizStatuses.iv = true;
						this.refreshQuiz('to_main_screen');
					});
			} else {
				this.shareService.getSelectOpeningMode
					.pipe(
						skip(1),
						filter((selectOpeningMode) => !selectOpeningMode),
						take(1),
					)
					.subscribe(() => {
						const quizModal = this.matDialog.getDialogById(QuizModalComponent.ID);
						quizModal?.close({ closeMethod: 'to_main_screen' });
					});
			}
		} else if (['project_sample', 'live', VIEW_TYPES.image_visualisation].includes(context)) {
			if (!this.quizStatuses.iv) {
				this.quizStatuses.iv = true;
				this.refreshQuiz('new_background');
			}
		} else if (context === 'no_background' || context === VIEW_TYPES.design) {
			if (!this.quizStatuses.dyb) {
				this.quizStatuses.dyb = true;
				this.refreshQuiz('view_change');
			}
		} else if (context === 'detection_error') {
			// this.quizStatuses.detection = true; // if this is here I don't think there is a point in having the restart detection path as it will be unreachable.
			this.refreshQuiz('detection_error');
		} else if (context === 'add_detection') {
			if (!this.quizStatuses.detection) {
				// should consider adding || isQuizReviewed to conditions so the quiz will be shown if they haven't reviewed it yet.
				this.quizStatuses.detection = true; // this is practically unreachable as this.quizStatuses.detection will always have been set to true by this point
				this.refreshQuiz('add_detection');
			} else {
				this.shareService.getSelectOpeningMode
					.pipe(
						filter((selectOpeningMode) => !selectOpeningMode),
						take(1),
					)
					.subscribe(() => this.matDialog.getDialogById(QuizModalComponent.ID)?.close({ closeMethod: 'to_main_screen' }));
			}
		} else if (context === 'restart_detection') {
			// Predicated on the fact that the getSelectOpeningMode observable is still active from the upload context and will handle the iv quiz if needed
			if (!this.quizStatuses.detection) {
				let detectionError = false;
				this.shareService.getDetectionError.pipe(take(1)).subscribe(() => (detectionError = true));
				this.shareService.getDetectionDone
					.pipe(
						skip(1),
						filter((detectionDone) => detectionDone),
						take(1),
						filter(() => !detectionError),
					)
					.subscribe(() => {
						this.quizStatuses.detection = true;
						this.refreshQuiz();
					});
			}
		}
	}

	refreshQuiz(context: string = null): void {
		const quizModal = this.matDialog.getDialogById(QuizModalComponent.ID);
		const closeMethod =
			context === 'to_main_screen' ? context : context === 'view_change' ? context : context === 'new_background' ? context : '';
		quizModal?.close({ closeMethod });

		if (quizModal) {
			quizModal.afterClosed().subscribe(() => this.openQuiz(context));
		} else {
			this.openQuiz(context);
		}
	}

	openQuiz(context: string = null): void {
		if (this.matDialog.getDialogById(QuizModalComponent.ID)) {
			return;
		}

		this.matDialog.open(QuizModalComponent, {
			...this.modalService.getConfig(QuizModalComponent.ID),
			scrollStrategy: this.overlay.scrollStrategies.noop(),
			data: { context },
		});

		this.sessionStorageService.setBlindData(this.quizStatuses, STORAGE_NAMES.zip_quiz_status);
	}
}
